<script lang="ts" setup>
  import type { Paginator } from '@othercommerce/domain';
  import { useBreakpoints } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';

  interface Offer {
    id: number;
    status: 'NEW' | 'PROCESSING' | 'ACCEPTED' | 'REJECTED';
    countryCode: string;
    email: number;
    phone: string;
    createdAt: string;
    updatedAt: string;
  }

  interface Props {
    offers: Paginator<Offer>;
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });


  defineProps<Props>();

  const breakpoints = useBreakpoints();
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.SALE_OFFERS') }}
  </AccountMobileNavigation>
  <SfTile v-if="offers.data.length > 0" class="account-orders" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">{{ $t('SHOP.ACCOUNT.HEADER.SALE_OFFERS') }}</h1>
    </template>
    <template #default>
      <div class="account-orders__content">
        <SfListCard v-for="offer in offers.data" :key="offer.id" class="account-orders">
          <template #icons>
            <SfOfferStatus :status="offer.status" />
          </template>
          <template #default>
            <div class="account-orders__header">
              <h2 class="account-orders__heading">
                {{ $t('SHOP.HEADER.SALE_OFFER_LISTING_NUMBER', { number: offer.id }) }}
              </h2>
              <p class="account-orders__date">
                <SfDateTime :value="offer.createdAt" />
              </p>
            </div>
          </template>
          <template #actions>
            <SfButton :href="$route('web.account.offers.show', { offer })" class="account-orders__details">
              {{ $t('SHOP.ACTION.SHOW_DETAILS') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
    </template>
  </SfTile>

  <SfTile v-else>
    <SfDataPlaceholder>
      {{ $t('SHOP.ACCOUNT.MESSAGE.NO_SALES_OFFERS') }}
    </SfDataPlaceholder>
  </SfTile>
  <SfPagination :pagination="offers" />

  <RouterView />
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-orders {

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
      flex-wrap: wrap;
    }

    &__heading {
      font-size: 1.25rem;
      word-break: break-all;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__date,
    &__count {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }

    &__price {
      font-weight: var(--font-weight-bold);
      margin-top: 0.125rem;
    }

    &__details {
      margin-top: 1rem;
    }
  }

  @include media.query(lg) {
    .account-orders {
      row-gap: 1rem;

      &__header {
        flex-direction: column;
        align-items: flex-start;
      }

      &__heading {
        font-size: 1rem;
      }

      &__content {
        gap: 0.75rem;

        .list-card__actions {
          gap: 0.75rem;
        }
      }

      .list-card__content-default {
        gap: 1rem;
      }

      &__details {
        margin-top: 0;
      }
    }
  }
</style>
